import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import styles from "./adminSetting.module.scss";
import {
  mdiAlertCircleOutline,
  mdiCheck,
  mdiClose,
  mdiPercentOutline,
} from "@mdi/js";
import Icon from "@mdi/react";
import adminSettingService from "../../../../api/services/AdminSettingService";
import { useClient } from "../../../../utils/ClientContext";
import { CustomSwitch, Loader } from "../../../../components";

const AdminSetting: React.FC = () => {
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState<"success" | "error">("success");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const userId = Number(localStorage.getItem("userId"));
  const { client } = useClient();
  const clientId = client.id;
  const [isInventoryIdAvailable, setIsInventoryIdAvailable] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  // Dynamically assign custom icons based on severity
  const getIcon = () => {
    if (severity === "success") {
      return <Icon path={mdiCheck} size={1} />; // Custom success icon
    }
    return <Icon path={mdiAlertCircleOutline} size={1} />; // Custom error icon
  };

  //Handle close snackbar
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // Create a state variable for the input value
  const [formValues, setFormValues] = useState({
    inventoryId: 0,
    inventorySettingId: 0,
    auditThreshold: "",
    erroredTags: "",
    errorPercentage: "",
    defaultMisfireThreshold: "",
    defaultMisfirePercentage: "",
    IsCartoned: false, 
    IsPalletized: false,
  });

  // Create a state variable for the error flags
  const [errors, setErrors] = useState<{
    auditThreshold: boolean | string;
    erroredTags: boolean;
    errorPercentage: boolean;
    defaultMisfireThreshold: boolean;
    defaultMisfirePercentage: boolean;
  }>({
    auditThreshold: false,
    erroredTags: false,
    errorPercentage: false,
    defaultMisfireThreshold: false,
    defaultMisfirePercentage: false,
  });

  // Validate Form
  const validateForm = () => {
    const newErrors: any = {};
    const auditThresholdValue = Number(formValues.auditThreshold);
    if (formValues.auditThreshold === "") {
      newErrors.auditThreshold = "Audit threshold is required.";
    } else if (isNaN(auditThresholdValue)) {
      newErrors.auditThreshold = "Audit threshold must be a number.";
    } else if (auditThresholdValue < 1 || auditThresholdValue > 100) {
      newErrors.auditThreshold = "Audit threshold must be between 1 and 100.";
    } else {
      newErrors.auditThreshold = false;
    }
    setErrors(newErrors);
    return !newErrors.auditThreshold;
  };

  // Fetch existing settings
  useEffect(() => {
    setShowLoader(true);
    const fetchInventorySettings = async () => {
      try {
        const response = await adminSettingService.getInventorySetting();
        if (response.isSuccess && response.inventorySettings) {
          setFormValues((prevState) => ({
            ...prevState,
            inventoryId: response.inventorySettings.inventoryId ?? 0,
            inventorySettingId:
              response.inventorySettings.inventorySettingId ?? 0,
            auditThreshold: (
              response.inventorySettings.auditThreshold ?? 0
            ).toString(),
            IsPalletized: response.inventorySettings.isPalletized ?? false, 
            IsCartoned: response.inventorySettings.isCartoned ?? false, 
          }));
          if (Boolean(response.inventorySettings.inventoryId ?? 0)) {
            setIsInventoryIdAvailable(true);
          } else {
            setIsInventoryIdAvailable(false);
          }
        } else {
          setOpenSnackbar(true);
          setSeverity("error");
          setSnackbarMessage(response.error.join(""));
          setTimeout(() => {
            handleCloseSnackbar();
          }, 10000);
        }
      } catch (error: any) {
        const errorMessages = error.response?.data?.errors || [
          "Error occurred while getting settings.",
        ];
        setOpenSnackbar(true);
        setSeverity("error");
        setSnackbarMessage(errorMessages.join(", "));
        setTimeout(() => {
          handleCloseSnackbar();
        }, 10000);
      }finally {
        setShowLoader(false);
      }
    };

    fetchInventorySettings();
  }, [clientId]);

  //Handle input change
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (name === "auditThreshold") {
      const numberValue = parseFloat(value);

      if (value === "") {
        setFormValues((prevValues) => ({
          ...prevValues,
          [name]: "",
        }));
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: "Audit threshold is required.",
        }));
      } else if (isNaN(numberValue) || !Number.isInteger(numberValue)) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: "Audit threshold must be a whole number.",
        }));
      } else if (numberValue < 1 || numberValue > 100) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: "Audit threshold must be between 1 and 100.",
        }));
      } else {
        setFormValues((prevValues) => ({
          ...prevValues,
          [name]: value,
        }));
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: false,
        }));
      }
    } else {
      setFormValues((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: false,
      }));
    }
  };

  //Handle save button
  const handleEditButtonClick = async () => {
    if (validateForm()) {
      setShowLoader(true);
      if (formValues.auditThreshold === "") {
        setErrors((prevErrors) => ({ ...prevErrors, auditThreshold: true }));
        return;
      }
      try {
        let response: any;
        if (formValues.inventorySettingId !== 0) {
          const settingUpdateData = {
            inventorySettingId: formValues.inventorySettingId,
            threshold: Number(formValues.auditThreshold),
            clientId: clientId,
            userId: userId,
          };
          response = await adminSettingService.updateInventorySetting(
            settingUpdateData
          );
        } else {
          const settingData = {
            inventoryId: formValues.inventoryId,
            threshold: formValues.auditThreshold,
            clientId: clientId,
            userId: userId,
          };
          response = await adminSettingService.addInventorySetting(settingData);
          setFormValues((prevState) => ({
            ...prevState,
            inventorySettingId: response.inventorySettingId,
          }));
        }
        if (response.isSuccess) {
          setOpenSnackbar(true);
          setSeverity("success");
          setSnackbarMessage("Settings saved successfully.");
          setTimeout(() => {
            handleCloseSnackbar();
          }, 1000);
        } else {
          setOpenSnackbar(true);
          setSeverity("error");
          setSnackbarMessage(response.error.join(""));
          setTimeout(() => {
            handleCloseSnackbar();
          }, 10000);
        }
      } catch (error: any) {
        const errorMessages = error.response?.data?.errors || [
          "Error occurred while saving settings.",
        ];
        setOpenSnackbar(true);
        setSeverity("error");
        setSnackbarMessage(errorMessages.join(", "));
        setTimeout(() => {
          handleCloseSnackbar();
        }, 10000);
      } finally {setShowLoader(false);
        setOpenSnackbar(true);
      }
    }
  };

  return (
    <div className="content_container">
      <Grid container spacing={2} size={{sm:12, xs:12}}>
        <Grid
          container
          size={{sm:12}}
          justifyContent="space-between"
          alignItems="center"
        >
          <h3 className={styles.page_title}>Inventory Setting</h3>
          <Button
            variant="contained"
            onClick={handleEditButtonClick}
            disabled={!isInventoryIdAvailable}
          >
            Save
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={2} size={{sm:12, xs:12}}>
        <Grid container size={{sm:10, xs:12}}>
          <Grid container  size={{sm:12, xs:12}} spacing={2}>
            <Grid size={{sm:6, xs:12}}>
              <FormControl className="fieldWrapper" fullWidth>
                <TextField
                  id="auditThreshold"
                  name="auditThreshold"
                  value={formValues.auditThreshold}
                  label="Audit Threshold"
                  placeholder="Audit Threshold"
                  onChange={handleChange}
                  error={Boolean(errors.auditThreshold)}
                  helperText={errors.auditThreshold || ""}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Icon path={mdiPercentOutline} size={0.8} />
                      </InputAdornment>
                    ),
                    inputProps: { min: 1, max: 100, step: 1, type: "number" },
                  }}
                  disabled={!isInventoryIdAvailable}
                />
              </FormControl>
              <Grid size={{sm:12}}>
  <FormControl className="fieldWrapper" fullWidth>
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography>Carton Tracking</Typography>
      <CustomSwitch
                   isActive={formValues.IsCartoned}
                   isConfirmationOnActive={false}
                   isConfirmationOnInActive={false}
                   ReadonlyTitle="This option can only be changed when creating a new inventory"
                   IsReadOnly={true}      />
    </Stack>
  </FormControl>
</Grid>
<Grid size={{sm:12}}>
  <FormControl className="fieldWrapper" fullWidth>
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography>Pallet Tracking &nbsp;</Typography>
      <CustomSwitch
                   isActive={formValues.IsPalletized}
                   isConfirmationOnActive={false}
                   isConfirmationOnInActive={false}
                   ReadonlyTitle="This option can only be changed when creating a new inventory"
                   IsReadOnly={true}      />
    </Stack>
  </FormControl>
</Grid>
          </Grid>
        </Grid> </Grid>
      </Grid>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={openSnackbar}
        autoHideDuration={10000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={severity}
          icon={getIcon()}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {showLoader && <Loader/>}
    </div>
  );
};
export default AdminSetting;
